import { Head, Link, useForm, usePage } from "@inertiajs/react"
import { Copy, Info, X } from "lucide-react"
import { useState } from "react"
import { toast } from "sonner"

import { Alert, AlertDescription } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import { useOrgRoutes } from "@/hooks/use-org-routes"
import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem, Facility, SharedProps } from "@/types"

const LOCAL_PART_FORMAT = /^(?!.*\.\.)[a-z0-9](?:[a-z0-9._-]{0,62}[a-z0-9])?$/
const ALLOW_LIST_DOMAIN = /^@[a-z0-9.-]+\.[a-z]{2,}$/
const ALLOW_LIST_EMAIL = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/

const TIMEZONES = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Phoenix",
  "UTC",
]

interface Props {
  facility: Facility
  is_superadmin: boolean
  can_edit_allow_list: boolean
}

export default function EditFacility({
  facility,
  is_superadmin: _isSuperadmin,
  can_edit_allow_list: canEditAllowList,
}: Props) {
  const routes = useOrgRoutes()
  // Org-level subdomain ships through the shared Inertia prop. The
  // facility only owns the local-part; the org owns the @subdomain half.
  const orgSubdomain =
    usePage<SharedProps>().props.organization?.email_subdomain || ""

  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Dashboard", href: routes.dashboardPath() },
    { title: facility.name, href: routes.facilityPath(facility.id) },
    { title: "Edit", href: routes.facilityPath(facility.id) },
  ]

  const { data, setData, patch, processing, errors } = useForm<{
    name: string
    timezone: string
    address: string
    description: string
    email_local_part: string
    inbound_allowed_senders: string[]
  }>({
    name: facility.name,
    timezone: facility.timezone || "America/New_York",
    address: facility.address || "",
    description: facility.description || "",
    email_local_part: facility.email_local_part || "",
    inbound_allowed_senders: facility.inbound_allowed_senders ?? [],
  })

  const [newSender, setNewSender] = useState("")
  const [senderError, setSenderError] = useState<string | null>(null)

  const localPartIsValid =
    data.email_local_part.length === 0 ||
    LOCAL_PART_FORMAT.test(data.email_local_part)

  function addSender() {
    const entry = newSender.trim().toLowerCase()
    if (!entry) return
    if (!ALLOW_LIST_DOMAIN.test(entry) && !ALLOW_LIST_EMAIL.test(entry)) {
      setSenderError(
        "Enter a full email (name@example.com) or a domain (@example.com).",
      )
      return
    }
    if (data.inbound_allowed_senders.includes(entry)) {
      setSenderError("Already in the list.")
      return
    }
    setData("inbound_allowed_senders", [...data.inbound_allowed_senders, entry])
    setNewSender("")
    setSenderError(null)
  }

  function removeSender(entry: string) {
    setData(
      "inbound_allowed_senders",
      data.inbound_allowed_senders.filter((s) => s !== entry),
    )
  }

  async function copyAddress(address: string) {
    try {
      await navigator.clipboard.writeText(address)
      toast.success("Copied to clipboard", { description: address })
    } catch {
      toast.error("Couldn't copy — copy manually")
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    patch(routes.facilityPath(facility.id))
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Edit ${facility.name}`} />

      <div className="mx-auto w-full max-w-2xl p-4">
        <form onSubmit={handleSubmit} className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Facility details</CardTitle>
              <CardDescription>Name, timezone, and address.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Facility Name</Label>
                <Input
                  id="name"
                  value={data.name}
                  onChange={(e) => setData("name", e.target.value)}
                  required
                />
                {errors.name && (
                  <p className="text-destructive text-sm">{errors.name}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="timezone">Timezone</Label>
                <Select
                  value={data.timezone}
                  onValueChange={(val) => setData("timezone", val)}
                >
                  <SelectTrigger id="timezone">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIMEZONES.map((tz) => (
                      <SelectItem key={tz} value={tz}>
                        {tz}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="address">Address</Label>
                <Input
                  id="address"
                  placeholder="e.g. 123 Main St, Houston, TX"
                  value={data.address}
                  onChange={(e) => setData("address", e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Input
                  id="description"
                  placeholder="e.g. NW shed, customer trial unit"
                  value={data.description}
                  onChange={(e) => setData("description", e.target.value)}
                />
                {errors.description && (
                  <p className="text-destructive text-sm">
                    {errors.description}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Inbound email feeds document intake (see
              Api::V1::EmailIngestController). */}
          <InboundMailCard
            orgSubdomain={orgSubdomain}
            emailLocalPart={data.email_local_part}
            inboundAllowedSenders={data.inbound_allowed_senders}
            onLocalPartChange={(v) => setData("email_local_part", v)}
            errors={errors}
            canEditAllowList={canEditAllowList}
            localPartIsValid={localPartIsValid}
            newSender={newSender}
            setNewSender={setNewSender}
            senderError={senderError}
            setSenderError={setSenderError}
            addSender={addSender}
            removeSender={removeSender}
            copyAddress={copyAddress}
          />

          <div className="flex justify-end gap-2">
            <Button variant="outline" asChild>
              <Link href={routes.facilityPath(facility.id)}>Cancel</Link>
            </Button>
            <Button type="submit" disabled={processing}>
              Save Changes
            </Button>
          </div>
        </form>
      </div>
    </AppLayout>
  )
}

interface InboundMailCardProps {
  orgSubdomain: string
  emailLocalPart: string
  inboundAllowedSenders: string[]
  onLocalPartChange: (value: string) => void
  errors: Partial<Record<string, string | string[]>>
  canEditAllowList: boolean
  localPartIsValid: boolean
  newSender: string
  setNewSender: (v: string) => void
  senderError: string | null
  setSenderError: (v: string | null) => void
  addSender: () => void
  removeSender: (entry: string) => void
  copyAddress: (address: string) => void
}

function InboundMailCard({
  orgSubdomain,
  emailLocalPart,
  inboundAllowedSenders,
  onLocalPartChange,
  errors,
  canEditAllowList,
  localPartIsValid,
  newSender,
  setNewSender,
  senderError,
  setSenderError,
  addSender,
  removeSender,
  copyAddress,
}: InboundMailCardProps) {
  const localPart = emailLocalPart.trim()
  const hasLocalPart = localPart.length > 0 && localPartIsValid
  const hasOrgSubdomain = orgSubdomain.length > 0
  // Today everything sent here is treated as a BOL; when other doc
  // types arrive (scale tickets, COAs, …) the server-side detector
  // will branch internally — the address stays.
  const inboxAddress =
    hasLocalPart && hasOrgSubdomain
      ? `${localPart}@${orgSubdomain}.rebulk.com`
      : ""

  return (
    <Card>
      <CardHeader>
        <CardTitle>Inbound mail</CardTitle>
        <CardDescription>
          Receive bills of lading by email. The org owns the{" "}
          <span className="font-mono">
            @{orgSubdomain || "<subdomain>"}.rebulk.com
          </span>{" "}
          domain (set in Organization Settings); this facility picks its own
          inbox name below.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="email_local_part">Inbox name</Label>
          {!hasOrgSubdomain && (
            <Alert>
              <Info className="size-4" />
              <AlertDescription>
                A superadmin must set the org-level subdomain in Organization
                Settings before this facility can receive email.
              </AlertDescription>
            </Alert>
          )}
          <div className="flex items-stretch gap-0">
            <Input
              id="email_local_part"
              value={emailLocalPart}
              disabled={!canEditAllowList || !hasOrgSubdomain}
              onChange={(e) => {
                const cleaned = e.target.value
                  .toLowerCase()
                  .replace(/[^a-z0-9._-]/g, "")
                onLocalPartChange(cleaned)
              }}
              placeholder="your-facility-name"
              maxLength={64}
              className="rounded-r-none"
            />
            <span className="bg-muted text-muted-foreground inline-flex items-center rounded-r-md border border-l-0 px-3 font-mono text-sm">
              @{orgSubdomain || "<subdomain>"}.rebulk.com
            </span>
          </div>
          <p className="text-muted-foreground text-xs">
            The part of the address before the <code>@</code>. Letters, numbers,
            dots, underscores, or hyphens. 1–64 characters. Must be unique
            within the org.
          </p>
          {!localPartIsValid && (
            <p className="text-destructive text-sm">
              Use 1–64 letters, numbers, dots, underscores or hyphens. No
              leading/trailing punctuation, no consecutive dots.
            </p>
          )}
          {errors.email_local_part && (
            <p className="text-destructive text-sm">
              {errors.email_local_part}
            </p>
          )}
        </div>

        {inboxAddress ? (
          <div className="space-y-2">
            <Label>Your inbound address</Label>
            <div className="rounded-md border">
              <AddressRow
                label="Forward bills of lading here"
                address={inboxAddress}
                onCopy={() => copyAddress(inboxAddress)}
              />
            </div>
          </div>
        ) : (
          <p className="text-muted-foreground text-sm">
            Set an inbox name above to receive emails.
          </p>
        )}

        <Separator />

        <div className="space-y-3">
          <div>
            <Label>Allowed senders</Label>
            <p className="text-muted-foreground mt-1 text-xs">
              Anyone with one of these addresses (or a matching domain) can send
              bills of lading into this facility by email. Members of this
              facility are always allowed.
            </p>
          </div>

          {inboundAllowedSenders.length === 0 ? (
            <p className="text-muted-foreground rounded-md border border-dashed p-3 text-sm">
              No external senders. Only members of this facility can submit by
              email.
            </p>
          ) : (
            <div className="divide-y rounded-md border">
              {inboundAllowedSenders.map((entry) => {
                const isDomain = entry.startsWith("@")
                return (
                  <div
                    key={entry}
                    className="flex items-center justify-between gap-2 px-3 py-2"
                  >
                    <span className="font-mono text-sm">{entry}</span>
                    <div className="flex items-center gap-2">
                      {isDomain && <Badge variant="secondary">Domain</Badge>}
                      {canEditAllowList && (
                        <button
                          type="button"
                          onClick={() => removeSender(entry)}
                          aria-label={`Remove ${entry}`}
                          className="hover:bg-muted text-muted-foreground rounded-sm p-1"
                        >
                          <X className="size-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          {canEditAllowList ? (
            <div className="space-y-1">
              <div className="flex gap-2">
                <Input
                  value={newSender}
                  onChange={(e) => {
                    setNewSender(e.target.value)
                    if (senderError) setSenderError(null)
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault()
                      addSender()
                    }
                  }}
                  placeholder="name@example.com or @example.com"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={addSender}
                  disabled={!newSender.trim()}
                >
                  Add
                </Button>
              </div>
              {senderError && (
                <p className="text-destructive text-sm">{senderError}</p>
              )}
            </div>
          ) : (
            <p className="text-muted-foreground text-xs">
              Only facility admins can change this list.
            </p>
          )}

          {errors.inbound_allowed_senders && (
            <p className="text-destructive text-sm">
              {errors.inbound_allowed_senders}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

interface AddressRowProps {
  label: string
  address: string
  onCopy: () => void
}

function AddressRow({ label, address, onCopy }: AddressRowProps) {
  return (
    <div className="flex items-center justify-between gap-2 px-3 py-2">
      <div className="min-w-0">
        <p className="text-muted-foreground text-xs">{label}</p>
        <p className="truncate font-mono text-sm">{address}</p>
      </div>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={onCopy}
        aria-label={`Copy ${label} address`}
      >
        <Copy className="size-4" />
        <span className="sr-only">Copy</span>
      </Button>
    </div>
  )
}
